/**
 * Ad-click attribution that survives navigation.
 *
 * useLeadSource keeps the FIRST touch forever. Ad platforms need the LAST
 * paid click instead (Google Ads / Meta accept offline conversions up to
 * 90 days after the click), and the click ID is only in the URL of the page
 * the ad landed on — by the time someone fills /contact or registers, it's gone.
 *
 * captureAdClick() runs on every app load (TrackingInit) and stores the
 * latest click IDs + UTMs for 90 days. getLeadAttribution() merges URL → last
 * click → first touch → ad-platform cookies into one payload that forms send
 * with the lead, so the server can later upload the conversion to Google Ads
 * (gclid/gbraid/wbraid) and Meta CAPI (fbc/fbp + event_id for dedup).
 */

import { getStoredLeadSource } from "@/hooks/useLeadSource";
import { getOrCreateVisitorId } from "@/lib/visitor";

const LAST_TOUCH_KEY = "te_last_touch";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

export const CLICK_ID_KEYS = [
  "gclid", "gbraid", "wbraid", // Google Ads
  "fbclid",                    // Meta
  "msclkid",                   // Microsoft Ads
  "ttclid",                    // TikTok
  "li_fat_id",                 // LinkedIn
  "twclid",                    // X
] as const;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id"] as const;

export interface LastTouch {
  click_ids: Record<string, string>;
  utm: Record<string, string>;
  landing_page: string;
  referrer: string | null;
  captured_at: number;
  /** Meta click cookie value built from fbclid: fb.1.<ms>.<fbclid> */
  fbc?: string;
}

export interface LeadAttribution {
  // Flat legacy fields (contact-notify + n8n webhook already read these)
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
  fbclid: string;
  msclkid: string;
  ttclid: string;
  fbc: string;
  fbp: string;
  te_visitor_id: string;
  /** Full context for reporting (first touch, last click, timestamps). */
  attribution: Record<string, unknown>;
}

function readCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&")}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
}

/** gclid from Google's conversion-linker cookie (_gcl_aw = "GCL.<ts>.<gclid>"). */
function gclidFromCookie(): string {
  const v = readCookie("_gcl_aw");
  const parts = v.split(".");
  return parts.length >= 3 ? parts.slice(2).join(".") : "";
}

export function getLastTouch(): LastTouch | null {
  try {
    const raw = localStorage.getItem(LAST_TOUCH_KEY);
    if (!raw) return null;
    const lt = JSON.parse(raw) as LastTouch;
    if (!lt.captured_at || Date.now() - lt.captured_at > TTL_MS) {
      localStorage.removeItem(LAST_TOUCH_KEY);
      return null;
    }
    return lt;
  } catch {
    return null;
  }
}

/**
 * Store the current URL's ad click IDs / UTMs as the last touch.
 * A visit without click IDs or UTMs (direct, organic, internal nav) keeps the
 * previous paid touch — same "last non-direct click" rule the ad platforms use.
 */
export function captureAdClick(): LastTouch | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const click_ids: Record<string, string> = {};
    const utm: Record<string, string> = {};
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v) click_ids[k] = v.slice(0, 500);
    }
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 255);
    }
    if (!Object.keys(click_ids).length && !Object.keys(utm).length) return getLastTouch();

    const now = Date.now();
    const lt: LastTouch = {
      click_ids,
      utm,
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
      captured_at: now,
      ...(click_ids.fbclid ? { fbc: `fb.1.${now}.${click_ids.fbclid}` } : {}),
    };
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(lt));
    return lt;
  } catch {
    return null;
  }
}

/** Unique ID shared by the browser event and the server-side conversion (dedup). */
export function newEventId(prefix = "te"): string {
  try {
    return `${prefix}_${crypto.randomUUID()}`;
  } catch {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Everything a lead form should send. Precedence per field:
 * current URL → last paid touch (90d) → first touch → platform cookies.
 */
export function getLeadAttribution(): LeadAttribution {
  const params = new URLSearchParams(window.location.search);
  const last = getLastTouch();
  const first = getStoredLeadSource();
  const firstRaw = first?.raw_params ?? {};

  const pick = (k: string): string =>
    params.get(k) || last?.click_ids[k] || last?.utm[k] || firstRaw[k] || "";

  const fbclid = pick("fbclid");
  const fbc = readCookie("_fbc") || last?.fbc || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : "");

  return {
    utm_source: pick("utm_source") || first?.utm_source || "",
    utm_medium: pick("utm_medium") || first?.utm_medium || "",
    utm_campaign: pick("utm_campaign") || first?.utm_campaign || "",
    utm_term: pick("utm_term") || first?.utm_term || "",
    utm_content: pick("utm_content") || first?.utm_content || "",
    gclid: pick("gclid") || gclidFromCookie(),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid,
    msclkid: pick("msclkid"),
    ttclid: pick("ttclid"),
    fbc,
    fbp: readCookie("_fbp"),
    te_visitor_id: getOrCreateVisitorId(),
    attribution: {
      last_click: last
        ? { ...last.click_ids, ...last.utm, landing_page: last.landing_page, referrer: last.referrer, at: new Date(last.captured_at).toISOString() }
        : null,
      first_touch: first
        ? {
            source: first.source ?? null,
            medium: first.medium ?? null,
            utm_campaign: first.utm_campaign,
            landing_page: first.landing_page,
            referrer: first.referrer_url,
            at: first.first_seen_at,
          }
        : null,
    },
  };
}
